import { useAuthStore } from 'context/AuthStore'
import { AuthService } from 'lib/api'
import { CollectionService } from 'lib/api/services/collection.service'
import {
  contractInitialState,
  eventsInitialState,
  lawyerEventsInitialState,
  paginationInitialState
} from 'lib/constants/initial-states'
import { useResErrors } from 'lib/hooks'
import { parseJson } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { createContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

export const ContractStoreContext = createContext({
  contractStore: {},
  contractStoreFunc: {}
})

const ContractStoreProvider = (props) => {
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { authStoreFunc } = useAuthStore()
  const { standardCheck } = useResErrors()

  const [pagination, setPagination] = useState(paginationInitialState)
  const [managers, setManagers] = useState([])
  const [contractAll, setContractAll] = useState(contractInitialState)
  const [contract, setContract] = useState(contractInitialState.contract)
  const [comments, setComments] = useState([])
  const [events, setEvents] = useState(eventsInitialState)
  const [lawyerEvents, setLawyerEvents] = useState(lawyerEventsInitialState)
  const [eventPhoneNumbers, setEventPhoneNumbers] = useState([])
  const [statuses, setStatuses] = useState([])
  const [eventHistory, setEventHistory] = useState([])
  const [lawyerStatuses, setLawyerStatuses] = useState([])
  const [fileTypes, setFileTypes] = useState([])
  const [documents, setDocuments] = useState([])
  const [calls, setCalls] = useState([])

  useEffect(() => {
    if (id) {
      getById()
      if (authStoreFunc.isAdmin()) getManagers()
    }
  }, [id])

  useEffect(() => {
    if (contractAll?.id && authStoreFunc.isManager()) getPhoneNumbers()
  }, [contractAll?.id])

  const getById = async () => {
    if (!id) return
    authStoreFunc.setLoading(true)
    await CollectionService.getById(id)
      .then(res =>
        standardCheck(res, () => {
          setContractAll(res.data)
          setContract(res?.data?.contract)
          setComments(parseJson(res?.data?.comment))
        })
      )
      .finally(() => authStoreFunc.setLoading(false))
  }

  const getCalls = async () => {
    if (!id) return
    await CollectionService.getCalls(id)
      .then(res =>
        standardCheck(res, () => setCalls(res.data.list))
      )
  }

  const getManagers = async () => {
    const data = { limit: 200, isActive: true, roleCodes: ['MANAGER'], projectCode: 'COLLECTION' }
    await AuthService.getUsers(data).then(res => {
      if (res?.data?.userList?.length) setManagers(res?.data?.userList)
      else setManagers([])
    })
  }

  const getFileTypes = async () => {
    await CollectionService.getFileTypes()
      .then(res => standardCheck(res, () => setFileTypes(res.data.list)))
  }

  const getWlFiles = async (id) => {
    await CollectionService.getWlFiles(id)
      .then(res => standardCheck(res, () => setDocuments(res.data)))
  }

  const getPhoneNumbers = async () => {
    if (!contractAll?.id) return
    await CollectionService.getPhoneNumbers(contractAll?.id)
      .then(res => standardCheck(res, () => {
        if (res?.data) setEventPhoneNumbers(res?.data?.list)
      }))
  }

  const getStatuses = async () => {
    await CollectionService.getStatuses()
      .then(res => standardCheck(res, () => {
        if (res.data) setStatuses(res.data)
      }))
  }

  const findByContract = async () => {
    await CollectionService.findByContract(contract?.contractId)
      .then(res => standardCheck(res, () => {
        if (res.data) setEventHistory(res.data)
      }))
  }

  const getLawyerStatuses = async () => {
    await CollectionService.getLawyerStatuses()
      .then(res => standardCheck(res, () => {
        if (res.data) setLawyerStatuses(res.data)
      }))
  }

  const addComment = (comment) => {
    const data = { id: Number(id), comment }
    CollectionService.updateComments(data)
      .then(res => {
        if (res?.data?.comment) setComments(parseJson(res?.data?.comment))
      })
  }

  const createEvents = (onClose = () => {}, saveWorkListFile = () => {}) => {
    const data = {
      ...events,
      workListId: Number(id),
      description: events.description || undefined,
      contactType: events.contactType || undefined,
      dueDate: events.dueDate || undefined,
      type: events.type || undefined,
      eventTypeCode: events.eventTypeCode || undefined,
      eventResultCode: events.eventResultCode || undefined,
      phoneNumber: events.phoneNumber || undefined
    }

    CollectionService.createEvents(data)
      .then(res => {
        if (res.data) {
          enqueueSnackbar('Успешно 😸', { variant: 'success' })
          onClose()
          getById()
          if (events.eventResultCode === 'HANDED_OVER_TO_COURT') {
            saveWorkListFile()
          }
        } else {
          enqueueSnackbar(`😿 Что-то пошло не так, статус ${res?.status}`, { variant: 'error' })
        }
      })
  }

  const clearEvents = () => setEvents(eventsInitialState)
  const clearLawyerEvents = () => setLawyerEvents(lawyerEventsInitialState)

  const handleEventsChange = e => {
    const { name, value } = e.target
    setEvents(prev => ({ ...prev, [name]: value }))
  }

  const handleLawyerEventsChange = e => {
    const { name, value } = e.target
    setLawyerEvents(prev => ({ ...prev, [name]: value }))
  }

  const handleChangeEventsDate = date => {
    setEvents(prev => ({ ...prev, dueDate: date }))
  }

  const contractStore = {
    managers,
    contractAll,
    contract,
    comments,
    pagination,
    events,
    lawyerEvents,
    eventPhoneNumbers,
    statuses,
    eventHistory,
    lawyerStatuses,
    fileTypes,
    documents,
    calls
  }

  const contractStoreFunc = {
    setPagination,
    addComment,
    handleEventsChange,
    handleChangeEventsDate,
    createEvents,
    clearEvents,
    setEvents,
    setLawyerEvents,
    getById,
    getWlFiles,
    getFileTypes,
    getPhoneNumbers,
    getStatuses,
    findByContract,
    getLawyerStatuses,
    getCalls,
    clearLawyerEvents,
    handleLawyerEventsChange
  }

  return (
    <ContractStoreContext.Provider value={{ contractStore, contractStoreFunc }}>
      {props.children}
    </ContractStoreContext.Provider>
  )
}

export default ContractStoreProvider
